import { defineStore } from 'pinia';
import { CobrowseClientSession } from '~/app/cobrowse/classes/CobrowseClientSession';
import type { CobrowseClientStatus, RemoteCursor, SelectPreview } from '~/app/cobrowse/classes/CobrowseClientSession';
import { CobrowseRelayClient, CobrowseRelayError } from '~/app/cobrowse/classes/CobrowseRelayClient';
import type { ScreenPoint } from '~/app/cobrowse/classes/CommandExecutor';
import type { SessionEndReason, SessionRequestedPayload } from '~/app/cobrowse/protocol';
import { useAuthStore } from '~/stores/authStore';

const RESUMABLE_SESSION_KEY = 'cobrowse:session-id';

export interface EndedCobrowseSession {
    agentName: string | null;
    reason: SessionEndReason;
}

export type AcceptOutcome = { kind: 'started'; agentName: string } | { kind: 'failed' };

export const useCobrowseStore = defineStore('cobrowse', () => {
    const status = ref<CobrowseClientStatus | 'idle'>('idle');
    const sessionId = ref<string | null>(null);
    const agentName = ref<string | null>(null);
    const cursor = ref<RemoteCursor | null>(null);
    const selectPreview = ref<SelectPreview | null>(null);
    const lastClick = ref<{ point: ScreenPoint; at: number } | null>(null);
    const pendingRequest = ref<SessionRequestedPayload | null>(null);
    const endedSession = ref<EndedCobrowseSession | null>(null);
    const acceptOutcome = ref<AcceptOutcome | null>(null);
    const isActive = computed(() => status.value !== 'idle' && status.value !== 'ended');

    let session: CobrowseClientSession | null = null;

    function relay(): { client: CobrowseRelayClient; url: string; authorization: string } | null {
        const { usableToken } = useAuthStore();
        const relayUrl = useRuntimeConfig().public.cobrowseRelayUrl;

        if (!usableToken || !relayUrl) {
            return null;
        }

        return { client: new CobrowseRelayClient(relayUrl), url: relayUrl, authorization: usableToken };
    }

    async function join(id: string): Promise<void> {
        const connection = relay();

        if (isActive.value || !connection) {
            return;
        }

        session = new CobrowseClientSession(id, connection.url, connection.client, {
            onStatus: (nextStatus) => (status.value = nextStatus),
            onCursor: (remoteCursor) => (cursor.value = remoteCursor),
            onSelectPreview: (preview) => (selectPreview.value = preview),
            onClick: (point) => (lastClick.value = { point, at: Date.now() }),
            onEnded: (reason) => {
                if (reason !== 'connection-lost') {
                    sessionStorage.removeItem(RESUMABLE_SESSION_KEY);
                    endedSession.value = { agentName: agentName.value, reason };
                }
                reset();
            },
        });
        sessionId.value = id;

        try {
            const joined = await session.join(connection.authorization);
            agentName.value = joined.agentName;
            sessionStorage.setItem(RESUMABLE_SESSION_KEY, id);
        } catch (error) {
            reset();
            throw error;
        }
    }

    async function resume(): Promise<void> {
        const resumableSessionId = sessionStorage.getItem(RESUMABLE_SESSION_KEY);

        if (!resumableSessionId) {
            return;
        }

        try {
            await join(resumableSessionId);
        } catch (error) {
            sessionStorage.removeItem(RESUMABLE_SESSION_KEY);

            if (!(error instanceof CobrowseRelayError)) {
                throw error;
            }
        }
    }

    function receiveRequest(request: SessionRequestedPayload): void {
        if (isActive.value || request.expiresAt <= Date.now()) {
            return;
        }

        pendingRequest.value = request;
    }

    async function fetchPendingRequest(): Promise<void> {
        const connection = relay();

        if (!connection || isActive.value || pendingRequest.value) {
            return;
        }

        try {
            const pending = await connection.client.pending(connection.authorization);

            if (pending) {
                receiveRequest(pending);
            }
        } catch (error) {
            if (!(error instanceof CobrowseRelayError)) {
                throw error;
            }
        }
    }

    function dismissRequest(requestSessionId: string): void {
        if (pendingRequest.value?.sessionId === requestSessionId) {
            pendingRequest.value = null;
        }
    }

    async function acceptRequest(): Promise<void> {
        const request = pendingRequest.value;
        pendingRequest.value = null;

        if (!request) {
            return;
        }

        try {
            await join(request.sessionId);
            acceptOutcome.value = { kind: 'started', agentName: request.agentName };
        } catch (error) {
            acceptOutcome.value = { kind: 'failed' };

            if (!(error instanceof CobrowseRelayError)) {
                throw error;
            }
        }
    }

    async function onTenantReady(): Promise<void> {
        await resume();
    }

    async function declineRequest(): Promise<void> {
        const request = pendingRequest.value;
        const connection = relay();
        pendingRequest.value = null;

        if (request && connection) {
            await connection.client.decline(request.sessionId, connection.authorization);
        }
    }

    function leave(): void {
        session?.leave();
    }

    function reset(): void {
        session = null;
        status.value = 'idle';
        sessionId.value = null;
        agentName.value = null;
        cursor.value = null;
        selectPreview.value = null;
        lastClick.value = null;
    }

    return {
        status,
        sessionId,
        agentName,
        cursor,
        selectPreview,
        lastClick,
        pendingRequest,
        endedSession,
        acceptOutcome,
        isActive,
        join,
        resume,
        receiveRequest,
        fetchPendingRequest,
        dismissRequest,
        acceptRequest,
        declineRequest,
        onTenantReady,
        leave,
    };
});
